import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, Plus, ImageIcon, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "@/cabinet/i18n";
import { api, formatApiError } from "@/cabinet/lib/api";
import PageHeader from "@/cabinet/components/layout/PageHeader";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "@/components/ui/sheet";

function formatDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "ka" ? "ka-GE" : "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Maps backend status -> tailwind classes for the badge.
const STATUS_STYLES = {
  pending: "bg-[rgba(234,196,144,0.55)] text-[hsl(var(--foreground))]",
  sourced: "bg-[rgba(169,184,154,0.55)] text-[hsl(var(--foreground))]",
  ordered: "bg-[rgba(120,170,200,0.45)] text-[hsl(var(--foreground))]",
  declined: "bg-[hsl(var(--destructive)/0.18)] text-[hsl(var(--destructive))]",
};

export default function MyRequestsPage() {
  const { t, lang } = useI18n();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.title = "SmartPaw — " + t("requests.title");
  }, [t]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/product-requests/mine");
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="my-requests-page">
      <PageHeader
        title={t("requests.title")}
        subtitle={t("requests.subtitle")}
        testIdPrefix="my-requests"
        action={
          <Button
            asChild
            className="rounded-xl bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90"
            data-testid="my-requests-new-button"
          >
            <Link to="/catalogue/request">
              <Plus className="mr-1.5 h-4 w-4" />
              {t("requests.new_request")}
            </Link>
          </Button>
        }
      />

      {loading ? (
        <div className="space-y-3" data-testid="my-requests-loading">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-2xl" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <Card className="border-dashed bg-paper" data-testid="my-requests-empty">
          <CardContent className="p-8 text-center">
            <PackageSearch className="mx-auto mb-3 h-8 w-8 text-[hsl(var(--muted-foreground))]" />
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {t("requests.empty")}
            </p>
            <Button
              asChild
              className="mt-4 rounded-xl bg-[hsl(var(--terracotta))] text-white hover:bg-[hsl(var(--terracotta))]/90"
              data-testid="my-requests-empty-cta"
            >
              <Link to="/catalogue/request">
                <Plus className="mr-1.5 h-4 w-4" />
                {t("requests.new_request")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[hsl(var(--border))] bg-paper" data-testid="my-requests-list">
          <CardContent className="p-0">
            <ul className="divide-y divide-[hsl(var(--border))]">
              {requests.map((r) => {
                const statusKey = r.status || "pending";
                return (
                  <li
                    key={r.request_id}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-4 sm:grid-cols-[auto_1fr_140px_120px_auto]"
                    data-testid={`my-requests-row-${r.request_id}`}
                  >
                    {/* Thumbnail or fallback */}
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-cream sm:h-14 sm:w-14">
                      {r.photo ? (
                        <img
                          src={r.photo}
                          alt={r.product_name || "request"}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[hsl(var(--muted-foreground))]">
                          <ImageIcon className="h-5 w-5" />
                        </div>
                      )}
                    </div>

                    {/* Product info */}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {r.product_name || "—"}
                      </div>
                      <div className="truncate text-xs text-[hsl(var(--muted-foreground))]">
                        {[r.brand, r.size, r.quantity ? `${t("requests.qty_short")}${r.quantity}` : null]
                          .filter(Boolean)
                          .join(" • ") || t("requests.no_details")}
                      </div>
                    </div>

                    <div className="hidden text-sm text-[hsl(var(--muted-foreground))] sm:block">
                      {formatDate(r.created_at, lang)}
                    </div>

                    <div className="hidden sm:block">
                      <Badge
                        variant="secondary"
                        className={`rounded-full ${STATUS_STYLES[statusKey] || STATUS_STYLES.pending}`}
                        data-testid={`my-requests-status-${r.request_id}`}
                      >
                        {t(`requests.status.${statusKey}`)}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-end">
                      <Sheet>
                        <SheetTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl"
                            onClick={() => setSelected(r)}
                            data-testid={`my-requests-view-${r.request_id}`}
                          >
                            {t("requests.view")}
                          </Button>
                        </SheetTrigger>
                        <SheetContent
                          className="w-full sm:max-w-lg overflow-y-auto"
                          data-testid="my-requests-detail-panel"
                        >
                          <SheetHeader>
                            <SheetTitle className="font-serif text-2xl">
                              {(selected || r).product_name}
                            </SheetTitle>
                            <SheetDescription>
                              {t("requests.request_id")}: {(selected || r).request_id}
                            </SheetDescription>
                          </SheetHeader>

                          {(() => {
                            const d = selected || r;
                            const sk = d.status || "pending";
                            return (
                              <div className="mt-5 space-y-4 text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="text-[hsl(var(--muted-foreground))]">
                                    {t("requests.col.status")}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className={`rounded-full ${STATUS_STYLES[sk] || STATUS_STYLES.pending}`}
                                  >
                                    {t(`requests.status.${sk}`)}
                                  </Badge>
                                </div>

                                <div className="flex items-center justify-between">
                                  <span className="text-[hsl(var(--muted-foreground))]">
                                    {t("requests.col.submitted")}
                                  </span>
                                  <span className="font-medium">
                                    {formatDate(d.created_at, lang)}
                                  </span>
                                </div>

                                {d.status_note && (
                                  <div className="rounded-xl border border-[hsl(var(--border))] bg-cream p-3">
                                    <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                                      <AlertCircle className="h-3.5 w-3.5" />
                                      {t("requests.admin_note")}
                                    </div>
                                    <p className="whitespace-pre-wrap text-sm">
                                      {d.status_note}
                                    </p>
                                  </div>
                                )}

                                {d.photo && (
                                  <div>
                                    <div className="mb-1.5 text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                                      {t("requests.col.photo")}
                                    </div>
                                    <img
                                      src={d.photo}
                                      alt={d.product_name}
                                      className="max-h-64 w-full rounded-xl object-contain bg-cream"
                                    />
                                  </div>
                                )}

                                <div className="space-y-2 rounded-xl bg-cream p-3">
                                  {d.brand && (
                                    <div className="flex justify-between gap-3">
                                      <span className="text-[hsl(var(--muted-foreground))]">
                                        {t("requests.col.brand")}
                                      </span>
                                      <span className="font-medium">{d.brand}</span>
                                    </div>
                                  )}
                                  {d.size && (
                                    <div className="flex justify-between gap-3">
                                      <span className="text-[hsl(var(--muted-foreground))]">
                                        {t("requests.col.size")}
                                      </span>
                                      <span className="font-medium">{d.size}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between gap-3">
                                    <span className="text-[hsl(var(--muted-foreground))]">
                                      {t("requests.col.quantity")}
                                    </span>
                                    <span className="font-medium tabular-nums">
                                      {d.quantity ?? 1}
                                    </span>
                                  </div>
                                  {d.notes && (
                                    <div>
                                      <div className="mb-1 text-[hsl(var(--muted-foreground))]">
                                        {t("requests.col.notes")}
                                      </div>
                                      <p className="whitespace-pre-wrap">{d.notes}</p>
                                    </div>
                                  )}
                                </div>

                                <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-3 text-xs text-[hsl(var(--muted-foreground))]">
                                  {t("requests.contact_hint")}{" "}
                                  <span className="font-medium text-[hsl(var(--foreground))]">
                                    {d.email}
                                  </span>
                                  {d.phone ? ` · ${d.phone}` : null}
                                </div>
                              </div>
                            );
                          })()}
                        </SheetContent>
                      </Sheet>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
